import { EmploymentInfoForm } from '@/components/PageForm/MoreInfoForm/drawers/EmploymentInfo/helper';

import { EMPLOYMENT_STATUS_TEXT, PROFESSION_TEXT } from '@/constants/user';

import { Tenant } from '@/types/common';
import { Option } from '@/types/components/common';
import { EmploymentStatus, Profession, User } from '@/types/user';

import { convertToBoolean } from './string';

export type ProfessionQuestionConfig = {
  question: string;
  options: Option[];
  description?: string;
};

export function getProfessionQuestionForSelfEmployed(
  tenant?: Tenant,
): ProfessionQuestionConfig {
  switch (tenant) {
    case Tenant.Anguilla:
    case Tenant.BVI:
    case Tenant.SaintKittsNevis:
    case Tenant.Suriname:
    case Tenant.SintMaarten:
      return {
        question: 'professionQuestionText.questions.selfEmployed',
        options: [
          {
            value: Profession.SoleTrader,
            text: PROFESSION_TEXT[Profession.SoleTrader],
            icon: 'toolbox',
          },
          {
            value: Profession.PartnershipLimitedLiability,
            text: PROFESSION_TEXT[Profession.PartnershipLimitedLiability],
            icon: 'handshake',
          },
          {
            value: Profession.Unincorporated,
            text: PROFESSION_TEXT[Profession.Unincorporated],
            icon: 'user',
          },
        ],
      };
    case Tenant.Cayman:
      return {
        question: 'professionQuestionText.questions.selfEmployed',
        options: [
          {
            value: Profession.SoleTrader,
            text: PROFESSION_TEXT[Profession.SoleTrader],
            icon: 'toolbox',
          },
          {
            value: Profession.CYM_PartnershipLimitedLiability,
            text: PROFESSION_TEXT[Profession.CYM_PartnershipLimitedLiability],
            icon: 'handshake',
          },
          {
            value: Profession.CYM_Unincorporated,
            text: PROFESSION_TEXT[Profession.CYM_Unincorporated],
            icon: 'user',
          },
        ],
      };
    default:
      return {
        question: 'Select your type of self-employment',
        options: [
          {
            value: Profession.SoleTrader,
            text: PROFESSION_TEXT[Profession.SoleTrader],
            icon: 'toolbox',
          },
          {
            value: Profession.PartnershipLimitedLiability,
            text: PROFESSION_TEXT[Profession.PartnershipLimitedLiability],
            icon: 'handshake',
          },
          {
            value: Profession.Unincorporated,
            text: PROFESSION_TEXT[Profession.Unincorporated],
            icon: 'user',
          },
        ],
      };
  }
}

export function getUnemployedProfessions(): ProfessionQuestionConfig {
  return {
    question: 'professionQuestionText.questions.unEmployed',
    options: [
      {
        value: Profession.Student,
        text: PROFESSION_TEXT[Profession.Student],
        icon: 'student',
      },
      {
        value: Profession.Pensioner,
        text: PROFESSION_TEXT[Profession.Pensioner],
        icon: 'glasses',
      },
      {
        value: Profession.Housewife,
        text: PROFESSION_TEXT[Profession.Housewife],
        icon: 'house',
      },
      {
        value: Profession.Unemployed,
        text: PROFESSION_TEXT[Profession.Unemployed],
        icon: 'popcorn',
      },
    ],
  };
}

export function getEmploymentStatusOptions(): Option[] {
  return [
    {
      value: EmploymentStatus.Employed,
      text: EMPLOYMENT_STATUS_TEXT[EmploymentStatus.Employed],
      icon: 'buildings',
    },
    {
      value: EmploymentStatus.SelfEmployed,
      text: EMPLOYMENT_STATUS_TEXT[EmploymentStatus.SelfEmployed],
      icon: 'toolbox',
    },
    {
      value: EmploymentStatus.Unemployed,
      text: EMPLOYMENT_STATUS_TEXT[EmploymentStatus.Unemployed],
      icon: 'popcorn',
    },
  ];
}

export const RISK_SECTOR_CATEGORY_NONE = 'NONE';
export function getRiskSectorOptions(): {
  value: string;
  text: string;
  group: string;
  groupOrder: number;
}[] {
  return [
    {
      value: 'CASINOS_GAMING_BETTING_CONTROL',
      text: 'employment.riskSector.options.CASINOS_GAMING_BETTING_CONTROL',
      group: 'employment.riskSector.groups.GAMING_SECTOR',
      groupOrder: 99,
    },
    {
      value: 'LOTTERIES',
      text: 'employment.riskSector.options.LOTTERIES',
      group: 'employment.riskSector.groups.GAMING_SECTOR',
      groupOrder: 99,
    },
    {
      value: 'BETTING_SHOPS_POOL_BETTING',
      text: 'employment.riskSector.options.BETTING_SHOPS_POOL_BETTING',
      group: 'employment.riskSector.groups.GAMING_SECTOR',
      groupOrder: 99,
    },
    {
      value: 'DEALERS_PRECIOUS_METALS_STONES_MINERALS',
      text: 'employment.riskSector.options.DEALERS_PRECIOUS_METALS_STONES_MINERALS',
      group: 'employment.riskSector.groups.MINING_OR_EXTRACTIVE_SECTOR',
      groupOrder: 98,
    },
    {
      value: 'ATTORNEYS_NOTARIES_ACCOUNTANTS_AUDITORS',
      text: 'employment.riskSector.options.ATTORNEYS_NOTARIES_ACCOUNTANTS_AUDITORS',
      group: 'employment.riskSector.groups.PROFESSIONS',
      groupOrder: 97,
    },
    {
      value: 'REAL_ESTATE_AGENTS_BROKERS_DEVELOPERS',
      text: 'employment.riskSector.options.REAL_ESTATE_AGENTS_BROKERS_DEVELOPERS',
      group: 'employment.riskSector.groups.REAL_ESTATE_SECTOR',
      groupOrder: 96,
    },
    {
      value: 'ART_DEALERS',
      text: 'employment.riskSector.options.ART_DEALERS',
      group: 'employment.riskSector.groups.REAL_ESTATE_SECTOR',
      groupOrder: 96,
    },
    {
      value: 'JEWELERS',
      text: 'employment.riskSector.options.JEWELERS',
      group: 'employment.riskSector.groups.REAL_ESTATE_SECTOR',
      groupOrder: 96,
    },
    {
      value: 'MONEY_VALUE_TRANSFER_SERVICES',
      text: 'employment.riskSector.options.MONEY_VALUE_TRANSFER_SERVICES',
      group: 'employment.riskSector.groups.REAL_ESTATE_SECTOR',
      groupOrder: 96,
    },
    {
      value: 'MOTOR_VEHICLE_SALES',
      text: 'employment.riskSector.options.MOTOR_VEHICLE_SALES',
      group: 'employment.riskSector.groups.REAL_ESTATE_SECTOR',
      groupOrder: 96,
    },
    {
      value: 'TRUSTS_COMPANY_SERVICE_PROVIDERS',
      text: 'employment.riskSector.options.TRUSTS_COMPANY_SERVICE_PROVIDERS',
      group: 'employment.riskSector.groups.REAL_ESTATE_SECTOR',
      groupOrder: 96,
    },
    {
      value: RISK_SECTOR_CATEGORY_NONE,
      text: 'employment.riskSector.options.NONE',
      group: 'employment.riskSector.groups.REAL_ESTATE_SECTOR',
      groupOrder: 96,
    },
  ];
}

export function getRiskSectorFlag(
  riskSectorFlag: User['riskSectorFlag'] | null,
  riskCategory:
    | EmploymentInfoForm['employmentDetailed']['riskSectorCategory']
    | null,
): boolean | undefined {
  if (riskCategory) {
    return !(riskCategory === RISK_SECTOR_CATEGORY_NONE);
  }
  return convertToBoolean(riskSectorFlag);
}
